import { SharedUiColorName } from '@/shared/ui/theming';
import { PluralizePipe } from '@/utils/i18n/pluralize.pipe';
import { Component, type OnInit, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { FiltersAndTotalsInvoiceErrors } from '../../../../interfaces/Invoice.interface';
import { SharedUiColoredTextColorDirective } from '../../../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { LocalNumberPipe } from '../../../../pipes/localnumber.pipe';
import { VatModes } from '../../../../providers/budgets/budgets.constants';
import { DashboardingProvider } from '../../../../providers/dashboarding.provider';
import { InvoiceProvider } from '../../../../providers/invoice/invoice.provider';
import { ProjectsProvider } from '../../../../providers/projects/projects.provider';
import { LegacyWidgetCode } from '../../../../providers/rights.constants';
import { TrackingService } from '../../../../providers/tracking/tracking.service';
import { WidgetSize } from '../../../../providers/widgets.contants';
import { Page, RoutingService } from '../../../../services/routing/routing.service';
import { AbstractWidgetClass } from '../../AbstractWidget.class';
import { MiniWidgetIconCardComponent } from '../mini-widget-icon-card/mini-widget-icon-card';

@UntilDestroy()
@Component({
    styleUrls: ['./widget-mini-invoice-errors.scss'],
    selector: 'widget-mini-invoice-errors',
    templateUrl: 'widget-mini-invoice-errors.html',
    imports: [MiniWidgetIconCardComponent, SharedUiColoredTextColorDirective, LocalNumberPipe, TranslatePipe, PluralizePipe]
})
export class WidgetMiniInvoiceErrorsComponent extends AbstractWidgetClass<null, void> implements OnInit {
    private _routingService = inject(RoutingService);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _invoiceProvider = inject(InvoiceProvider);
    private _trackingService = inject(TrackingService);
    private _projectsProvider = inject(ProjectsProvider);

    size = WidgetSize.THIRD;

    invoiceErrorsTotal!: number;
    invoiceErrorsCount!: number;
    loading: boolean = true;
    statusColor: SharedUiColorName = SharedUiColorName.NEUTRAL;
    vatMode!: VatModes;

    ngOnInit() {
        combineLatest([this._dashboardingProvider.getSelectedNodeId$(), this._projectsProvider.getVatMode$()])
            .pipe(
                filter(([_, vatMode]) => !!vatMode),
                untilDestroyed(this)
            )
            .subscribe(([classificationNodeId, vatMode]) => {
                this.loading = true;
                this.vatMode = vatMode!;

                this._invoiceProvider
                    .getFiltersAndTotalsInvoiceErrors(classificationNodeId)
                    .then((filtersAndTotals: FiltersAndTotalsInvoiceErrors) => {
                        this.loading = false;

                        // Mixpanel tracking
                        this._trackingService.widgetViewed(LegacyWidgetCode.WDGT_MINI_INVOICE_ERRORS, {
                            classificationNodeId,

                            count: filtersAndTotals.totals.count,

                            errorPercentVatExcl: filtersAndTotals.totals.errorPercentVatExcl,
                            errorVatExcl: filtersAndTotals.totals.errorVatExcl,
                            originalVatExcl: filtersAndTotals.totals.originalVatExcl,

                            errorPercentVatIncl: filtersAndTotals.totals.errorPercentVatIncl,
                            errorVatIncl: filtersAndTotals.totals.errorVatIncl,
                            originalVatIncl: filtersAndTotals.totals.originalVatIncl
                        });

                        switch (vatMode) {
                            case VatModes.VAT_EXCL:
                                this.invoiceErrorsTotal = filtersAndTotals.totals.errorVatExcl;
                                break;
                            case VatModes.VAT_INCL:
                                this.invoiceErrorsTotal = filtersAndTotals.totals.errorVatIncl;
                                break;
                        }
                        this.invoiceErrorsCount = filtersAndTotals.totals.count;

                        if (this.invoiceErrorsTotal > 0) {
                            this.statusColor = SharedUiColorName.FAILURE;
                        } else {
                            this.statusColor = SharedUiColorName.SUCCESS;
                        }
                    });
            });
    }

    goToInvoiceErrorsPage() {
        this._routingService.goTo(Page.INVOICE_DISCREPANCY, {});
    }
}
